/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

const buildConversationsListParams = ({
  metric,
  from,
  to,
  type = 'account',
  id,
  page,
  groupBy,
  businessHours,
  conversationDirection,
  comunicanteCampaign,
}) => ({
  metric,
  since: from,
  until: to,
  type,
  id,
  page,
  group_by: groupBy,
  business_hours: businessHours,
  conversation_direction: conversationDirection,
  comunicante_campaign: comunicanteCampaign,
  timezone_offset: getTimeOffset(),
});

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    onlyAssigned = false,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        only_assigned: onlyAssigned,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  // eslint-disable-next-line default-param-last
  getSummary(
    since,
    until,
    // eslint-disable-next-line default-param-last
    type = 'account',
    id,
    groupBy,
    businessHours,
    conversationDirection,
    comunicanteCampaign
  ) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1, agentId = null) {
    let params = {
      type,
      page,
    };

    if (agentId) {
      params.id = agentId;
    }

    return axios.get(`${this.url}/conversations`, {
      params,
    });
  }

  getAgentReports({
    from: since,
    to: until,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/agents`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getConversationTrafficCSV({ onlyAssigned, comunicanteCampaign } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: {
        timezone_offset: getTimeOffset(),
        only_assigned: onlyAssigned,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getLabelReports({
    from: since,
    to: until,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/labels`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getInboxReports({
    from: since,
    to: until,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/inboxes`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getAgentsInboxes({
    from: since,
    to: until,
    conversationDirection,
    comunicanteCampaign,
    agentIds,
    inboxIds,
  }) {
    return axios.get(this.url + '/agents_inboxes', {
      params: {
        since,
        until,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
        agent_ids: agentIds,
        inbox_ids: inboxIds,
      },
    });
  }

  getAgentsInboxesCSV({
    from: since,
    to: until,
    conversationDirection,
    comunicanteCampaign,
    agentIds,
    inboxIds,
  }) {
    return axios.get(this.url + '/agents_inboxes.csv', {
      params: {
        since,
        until,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
        agent_ids: agentIds,
        inbox_ids: inboxIds,
      },
    });
  }

  getTeamReports({
    from: since,
    to: until,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/teams`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getBotMetrics({ from, to, conversationDirection, comunicanteCampaign } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: {
        since: from,
        until: to,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getBotSummary({
    from,
    to,
    groupBy,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
        conversation_direction: conversationDirection,
        comunicante_campaign: comunicanteCampaign,
      },
    });
  }

  getConversationsList({
    metric,
    from,
    to,
    type = 'account',
    id,
    page = 1,
    groupBy,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/conversations_list`, {
      params: buildConversationsListParams({
        metric,
        from,
        to,
        type,
        id,
        page,
        groupBy,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      }),
    });
  }

  getConversationsListCSV({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/conversations_list.csv`, {
      params: buildConversationsListParams({
        metric,
        from,
        to,
        type,
        id,
        groupBy,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      }),
    });
  }

  getConversationsListXLSX({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  }) {
    return axios.get(`${this.url}/conversations_list.xlsx`, {
      params: buildConversationsListParams({
        metric,
        from,
        to,
        type,
        id,
        groupBy,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      }),
      responseType: 'arraybuffer',
    });
  }

  getEntityReportXLSX(type, params) {
    return axios.get(`${this.url}/${type}.xlsx`, {
      params: {
        since: params.from,
        until: params.to,
        business_hours: params.businessHours,
        conversation_direction: params.conversationDirection,
        comunicante_campaign: params.comunicanteCampaign,
        agent_ids: params.agentIds,
        inbox_ids: params.inboxIds,
      },
      responseType: 'arraybuffer',
    });
  }
}

export default new ReportsAPI();
